/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";


import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {Location} from '../models/location';
import {LocationInstance} from '../models/location-instance';
import {PropertyPatch} from "../models/property-patch";
import {Function} from '../models/function';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';
import { FilterOption, buildFilterParams } from '../models/filter-option';

@Injectable()
export class LocationService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/locations/`;
  }


  getNumberLocations(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberLocationInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllLocationsForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Location>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<Location>>(callUrl);
  }

  getAllLocationInstancesForPagingWithFilters(start: number, size: number, order: string, filters: FilterOption[]): Promise<PagedResults<LocationInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=" + order + buildFilterParams(filters);
    return this.mRestService.get<PagedResults<LocationInstance>>(callUrl);
  }

  getAllLocationInstancesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<LocationInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<LocationInstance>>(callUrl);
  }


  getAllLocations(): Promise<Location[]> {
    const rootUrl = this.getUrl();
    return this.mRestService.get<Location[]>(rootUrl);
  }

  getLocationById(id: number): Promise<Location> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Location>(callUrl);
  }

  getAllFunctions(): Promise<Function[]> {
    const rootUrl = this.getUrl() + 'functions';
    return this.mRestService.get<Function[]>(rootUrl);
  }

  createLocation(location: Location): Promise<Location> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Location>(callUrl, location);
  }

  updateLocation(location: Location): Promise<Location> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Location>(callUrl, location);
  }

  deleteLocations(ids: number[]): Promise<any> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, ids);
  }





  getAllLocationInstances(): Promise<LocationInstance[]> {
    const rootUrl = this.getUrl() + 'instances';
    return this.mRestService.get<LocationInstance[]>(rootUrl);
  }

  getLocationInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<LocationInstance[]> {
    const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<LocationInstance[]>(url);
  } 

  getAllRootLocationInstances(): Promise<LocationInstance[]> {
    const rootUrl = this.getUrl() + 'instances/root';
    return this.mRestService.get<LocationInstance[]>(rootUrl);
  }

  getLocationInstanceById(id: number): Promise<LocationInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<LocationInstance>(callUrl);
  }

  getFunctionsForLocationInstance(id: number): Promise<Function[]> {
    const rootUrl = this.getUrl() + 'instances/' + `${id}` + '/functions';
    return this.mRestService.get<Function[]>(rootUrl);
  }

  createLocationInstance(locationInstance: LocationInstance): Promise<LocationInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.post<LocationInstance>(callUrl, locationInstance);
  }

  updateLocationInstance(locationInstance: LocationInstance): Promise<LocationInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.put<LocationInstance>(callUrl, locationInstance);
  }

  deleteLocationInstances(ids: number[]): Promise<any> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, ids);
  }

  executeFunction(locationInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${locationInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

  /*
  findAllAssetsInLocation(): Promise<void> {

  }

  findAllUsersInLocation(): Promise<void> {

  }
  */

}
